import { useState, useEffect, useRef } from 'react';
import { UiTable, UiButton, UiSearch, UiCounterBatch } from '../../../components';
import { CONFIG_COLUMNS } from './config';
import ConfigDrawer from './configdrawer/configDrawer';
import { CONFIGMANAGEMENT } from '../../../apiServices/endpoints';
import * as api from "../../../actions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from 'react-redux';
import { loaderReducer } from '../../../reducer/loader.reducer';
import { showSuccessNotification, showErrorNotification } from '../../../components/uiNotification';
import { searchInitiateDelayTime, tablePageSizeOptions } from '../../../utils/appUtils';

const Config = () => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const debounceTimeOut = useRef(null);
    const [screenState, setScreenState] = useState({
        page: 0,
        size: 10,
        searchFilter: "",
        searchTermValue: "",
    });
    const [drawerMode, setDrawerMode] = useState('add');
    const [showDrawer, setShowDrawer] = useState(false);
    const [configList, setConfigList] = useState([]);
    const [selectedConfigId, setSelectedConfigId] = useState(null);
    const configResults = useSelector((state) => state?.configAction?.configData);
    const configTypeOptions = configResults?.find((detail) => detail?.configId === "configType")?.configuration?.map((config) => ({ label: config, value: config }));
    const [formData, setFormData] = useState({
        configId: '',
        defaultValue: '',
        configValue: [],
        configValueInput: '',
        configType: null
    });

    const getAllConfig = (page, size, search) => api.get(`${CONFIGMANAGEMENT}/page?page=${page}&size=${size}${search ? `&commonSearch=${search}` : ""}`);
    const getConfigDetail = (configId) => api.get(`${CONFIGMANAGEMENT}/${configId}`);
    const createConfig = (payload) => api.post(`${CONFIGMANAGEMENT}`, payload);
    const updateConfig = (configId, payload) => api.put(`${CONFIGMANAGEMENT}/edit/${configId}`, payload);

    const { data: configData, isFetching, refetch } = useQuery(
        ["FETCH_ALL_CONFIG", screenState?.page, screenState?.size, screenState?.searchTermValue],
        () => getAllConfig(screenState?.page, screenState?.size, screenState?.searchTermValue),
        {
            onSuccess: (configResponse) => {
                if (configResponse?.statusCode === 200) {
                    setConfigList(configResponse?.result?.configResponsePage?.content);
                }
            },
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingConfigDetail } = useQuery(
        ["FETCH_CONFIG_DETAIL", selectedConfigId],
        () => getConfigDetail(selectedConfigId),
        {
            onSuccess: (response) => {
                if (response && response.configId) {
                    setFormData({
                        configId: response?.configId || '',
                        defaultValue: response?.defaultValue || '',
                        configValue: Array.isArray(response?.configuration) ? response?.configuration : [],
                        configValueInput: '',
                        configType: response?.configType || null
                    });
                    setDrawerMode('edit');
                    setShowDrawer(true);
                } else {
                    showErrorNotification("Error", response?.message || 'An error occurred while fetching config details');
                }
            },
            enabled: !!selectedConfigId,
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isCreatingConfig } = useQuery(
        ["CREATE_CONFIG", ""],
        () => createConfig({}),
        {
            onSuccess: (response) => {
                const statusCode = response?.statusCode || response?.data?.statusCode;
                if (statusCode === 200) {
                    showSuccessNotification("Success", "Config created successfully");
                    handleDrawerClose();
                    refetch();
                } else {
                    showErrorNotification("Error", response?.message || 'An error occurred while creating config');
                }
            },
            enabled: false,
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isUpdatingConfig } = useQuery(
        ["UPDATE_CONFIG", ""],
        () => updateConfig(formData?.configId, {}),
        {
            onSuccess: (response) => {
                const statusCode = response?.statusCode || response?.data?.statusCode;
                if (statusCode === 200) {
                    showSuccessNotification("Success", "Config updated successfully");
                    handleDrawerClose();
                    refetch();
                } else {
                    showErrorNotification("Error", response?.message || 'An error occurred while updating config');
                }
            },
            enabled: false,
            refetchOnWindowFocus: false
        }
    );

    const handleChange = (event, fieldName = null, isSelect = false) => {
        if (isSelect) {
            setFormData(prev => ({ ...prev, [fieldName]: event }));
            return;
        }
        const { name, value } = event.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleChipChange = (event, isRemove = false, removeValue = null) => {
        if (isRemove && removeValue !== null) {
            setFormData(prev => {
                const updatedConfigValue = prev?.configValue?.filter(val => val !== removeValue);
                const newDefaultValue = prev?.defaultValue === removeValue
                    ? (updatedConfigValue?.length > 0 ? updatedConfigValue?.[0] : '')
                    : prev?.defaultValue;
                return {
                    ...prev,
                    configValue: updatedConfigValue || [],
                    defaultValue: newDefaultValue
                };
            });
            return;
        }
        if (event && event.key === 'Enter') {
            event.preventDefault();
            const newValue = formData?.configValueInput?.trim();
            if (newValue) {
                if (!formData?.configValue?.includes(newValue)) {
                    const updatedConfigValue = [...formData?.configValue, newValue];
                    setFormData(prev => ({
                        ...prev,
                        configValue: updatedConfigValue,
                        configValueInput: '',
                        defaultValue: !prev?.defaultValue || !updatedConfigValue?.includes(prev?.defaultValue)
                            ? newValue
                            : prev?.defaultValue
                    }));
                } else {
                    setFormData(prev => ({ ...prev, configValueInput: '' }));
                }
            }
            return;
        }
        if (event && event.target) {
            const { name, value } = event.target;
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = () => {
        const payload = {
            configId: formData?.configId?.trim(),
            configuration: formData?.configValue,
            defaultValue: formData?.defaultValue?.trim(),
            configType: formData?.configType
        };
        if (drawerMode === 'add') {
            queryClient.prefetchQuery(["CREATE_CONFIG", ""], () => createConfig(payload));
        } else {
            queryClient.prefetchQuery(["UPDATE_CONFIG", ""], () => updateConfig(formData?.configId, payload));
        }
    };

    const handleAddClick = () => {
        setDrawerMode('add');
        setShowDrawer(true);
    };

    const handleDrawerClose = () => {
        setShowDrawer(false);
        setFormData({
            configId: '',
            defaultValue: '',
            configValue: [],
            configValueInput: '',
            configType: null
        });
        setSelectedConfigId(null);
    };

    const handleAction = (record) => {
        setSelectedConfigId(record.configId);
    };

    const handleConfigRowClick = (configDetail) => {
        handleAction(configDetail);
    };

    const handleSearch = (searchValue) => {
        const searchTerm = searchValue || "";
        setScreenState(prev => ({ ...prev, searchFilter: searchTerm }));
        debounceSearch(searchTerm);
    };

    const debounceSearch = (searchTerm) => {
        if (debounceTimeOut?.current) {
            clearTimeout(debounceTimeOut?.current);
        }
        if (searchTerm && searchTerm !== "") {
            debounceTimeOut.current = setTimeout(() => {
                setScreenState(prev => ({ ...prev, searchTermValue: searchTerm, page: 0 }));
            }, searchInitiateDelayTime);
        } else {
            setScreenState(prev => ({ ...prev, searchTermValue: "", page: 0 }));
        }
    };

    useEffect(() => {
        const isLoading = isFetching || isFetchingConfigDetail || isCreatingConfig || isUpdatingConfig;
        dispatch(loaderReducer(isLoading));
    }, [dispatch, isFetching, isFetchingConfigDetail, isCreatingConfig, isUpdatingConfig]);

    useEffect(() => {
        return () => {
            if (debounceTimeOut?.current) {
                clearTimeout(debounceTimeOut?.current);
            }
        };
    }, []);

    const totalConfigCount = configData?.result?.configResponsePage?.totalElements || 0;
    const shouldShowConfigPagination = totalConfigCount > 10;

    return (
        <div className="config-container">
            <div className="flexible">
                <div className="flexible">
                    <span>Config</span>
                    <UiCounterBatch>
                        {configData?.result?.configResponsePage?.totalElements || 0}
                    </UiCounterBatch>
                </div>
                <div className="flexible">
                    <UiSearch 
                        placeholder="Config ID" 
                        onChange={(event) => handleSearch(event?.target?.value)}
                        value={screenState?.searchFilter}
                        style={{ width: 200 }}
                    />
                    <UiButton 
                        add
                        type="primary" 
                        className="add-btn"
                        onClick={handleAddClick}
                    >
                        Add
                    </UiButton>
                </div>
            </div>
            <div>
                <UiTable 
                    columns={CONFIG_COLUMNS({ actionRender: handleAction })}
                    dataSource={configList}
                    scroll={{ y: "calc(100vh - 220px)" }}
                    onRow={(configDetail) => ({
                        onClick: () => handleConfigRowClick(configDetail)
                    })}
                    pagination={!shouldShowConfigPagination ? false : {
                        current: screenState?.page + 1,
                        pageSize: screenState?.size || 10,
                        total: totalConfigCount,
                        pageSizeOptions: tablePageSizeOptions,
                        showSizeChanger: true,
                        onChange: (page, size) => setScreenState(prev => ({ ...prev, page: page - 1, size: size }))
                    }}
                />
            </div>
            <ConfigDrawer 
                open={showDrawer}
                onClose={handleDrawerClose}
                formData={formData}
                handleChange={handleChange}
                handleChipChange={handleChipChange}
                handleSave={handleSave}
                isLoading={drawerMode === 'add' ? isCreatingConfig : isUpdatingConfig}
                configTypeOptions={configTypeOptions}
                mode={drawerMode}
            />
        </div>
    );
};

export default Config;
